/* eslint react-hooks/exhaustive-deps: 0 */
import { Checkbox, message, Select as AntdSelect } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { Button, Col, FormItem, Input, Row, Select } from 'components';
import Loading from 'components/ReturnOrderModal/Loading';
import { theme } from 'configs/styled';
import DriverPortalLayout from 'containers/DriverPortalPage/components/DriverPortalLayout';
import InputCheckBox from 'containers/DriverPortalPage/components/InputCheckBox';
import { getIn, useFormik } from 'formik';
import usePrompt from 'hooks/usePrompt';
import { useQueryParams } from 'hooks/useQueryParams';
import React, { useCallback, useEffect, useState } from 'react';
import {
  addressService,
  authenticationService,
  mainService,
} from 'services/driverPortal';
import {
  IEmergencyContact,
  ITaxAddress,
  STATUS_RESPONSE,
} from 'services/driverPortal/DriverPortal.interface';
import * as Yup from 'yup';
import { UpdatePersonalInfo } from '../../../../services/driverPortal/Address.interface';
import { PotentialLeadData } from '../../../../services/driverPortal/main/MainService.interface';
import { THAI_ONLY } from '../../../../utils/validate';
import Divider from '../../components/Divider';
import TextBold from '../../components/TextBold';
import TextRegular from '../../components/TextRegular';
import Title from '../../components/Title';
import ConfirmReturnModal from '../components/ConfirmReturnModal';
import ReturnButton from '../components/ReturnButton';

const getAuthHeader = (): Record<string, string> | undefined => {
  return undefined;
};

const digitsOnly = (s?: string) => (s || '').replace(/\D/g, '');
const parseStringList = (value: unknown): string[] => {
  if (Array.isArray(value))
    return value
      .map(String)
      .map(v => v.trim())
      .filter(Boolean);
  if (!value) return [];
  try {
    const parsed = JSON.parse(String(value));
    if (Array.isArray(parsed))
      return parsed
        .map(String)
        .map(v => v.trim())
        .filter(Boolean);
  } catch (_) {}
  return String(value)
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
};
const pickId = (
  options: Array<{ _id: string; title: string; value: string }>,
  val?: string,
) => {
  if (!val) return undefined;
  const f = options.find(o => o.title === val || o.value === val);
  return f ? Number(f._id) : undefined;
};

export interface IMainPageProps {}

export interface ISelectAddressProps {
  _id: string;
  title: string;
  value: string;
  tms_id?: string;
  disabled?: boolean;
  districtName?: string;
}

export interface StoreOption extends ISelectAddressProps {
  label?: React.ReactNode;
  data?: {
    code: string;
    name: string;
    addr: string;
    _search: string;
    raw: any;
  };
}

interface ISubDistrict {
  _id: string;
  title: string;
  value: string;
  postalcode: string;
  disabled?: boolean;
}

interface IPostalCode {
  _id: string;
  title: string;
  value: string;
}

export interface IPreferredServiceAreaSimple {
  province?: string;
  district?: string;
  districtId?: string;
  subDistrict?: string;
  subDistrictId?: string;
  storeCode?: string;
  storeName?: string;
}

interface IPersonalInformationRequest {
  taxAddress: ITaxAddress;
  emergencyContact: IEmergencyContact;
  preferredServiceArea?: IPreferredServiceAreaSimple;
  status?: string;
  isAllowEmergencyContact?: boolean;
}

const validatePersonalInfo = Yup.object().shape({
  taxAddress: Yup.object().shape({
    isAddressAccordingToIDCard: Yup.boolean(),
    province: Yup.string().when('isAddressAccordingToIDCard', {
      is: false,
      then: Yup.string().required('กรุณาเลือกจังหวัด'),
    }),
    postalCode: Yup.string().when('isAddressAccordingToIDCard', {
      is: false,
      then: Yup.string().required('กรุณาเลือกรหัสไปรษณีย์'),
    }),
    district: Yup.string().when('isAddressAccordingToIDCard', {
      is: false,
      then: Yup.string().required('กรุณาเลือกเขต/อำเภอ'),
    }),
    subDistrict: Yup.string().when('isAddressAccordingToIDCard', {
      is: false,
      then: Yup.string().required('กรุณาเลือกแขวง/ตำบล'),
    }),
    address: Yup.string().when('isAddressAccordingToIDCard', {
      is: false,
      then: Yup.string().required('กรุณากรอกที่อยู่'),
    }),
  }),
  emergencyContact: Yup.object().shape({
    name: Yup.string()
      .required('กรุณากรอกชื่อ - นามสกุล บุคคลที่ติดต่อได้ในกรณีฉุกเฉิน')
      .matches(THAI_ONLY, 'ใส่ได้เฉพาะอักษรไทย'),
    relationship: Yup.string().required('กรุณาเลือกความสัมพันธ์'),
    phone: Yup.string()
      .matches(/^[0-9]{10}/, 'เบอร์โทรศัพท์นี้ไม่ถูกต้อง')
      .required('กรุณากรอกเบอร์โทรศัพท์'),
  }),
  preferredServiceArea: Yup.object().shape({
    storeName: Yup.string(),
  }),
});

export const getAddressProvinces = async () => {
  try {
    return await addressService.provinces();
  } catch (err) {}
};

const PersonalInformationPage: React.FC<IMainPageProps> = () => {
  const { userId, accessToken } = authenticationService.getUserToken();

  const [provinceOptions, setProvinceOptions] = useState<ISelectAddressProps[]>(
    [],
  );
  const [districtsOptions, setDistrictsOptions] = useState<
    ISelectAddressProps[]
  >([]);
  const [subDistrictsOptions, setSubDistrictsOptions] = useState<
    ISubDistrict[]
  >([]);
  const [postalCodeOptions, setPostalCodeOptions] = useState<IPostalCode[]>([]);

  // service-area (single sequence)
  const [districtOptionsSA, setDistrictOptionsSA] = useState<
    ISelectAddressProps[]
  >([]);
  const [subDistrictOptionsSA, setSubDistrictOptionsSA] = useState<
    ISelectAddressProps[]
  >([]);
  const [storeOptionsSA, setStoreOptionsSA] = useState<StoreOption[]>([]);
  const [loadingStores, setLoadingStores] = useState<boolean>(false);

  const [prevCompany, setPrevCompany] = useState<number | undefined>(undefined);

  const findSubDistrictIdByTitle = (title: string): string | undefined => {
    const found = subDistrictsOptions.find(sd => sd.title === title);
    return found?._id;
  };

  const { search, pushUrl } = useQueryParams();
  const [loadingPage, setLoadingPage] = useState(true);
  const [disableEdit, setDisableEdit] = useState(false);
  // const { userId, accessToken } = authenticationService.getUserToken();
  const [isShowModal, setIsShowModal] = useState(false);
  const [, setShowPrompt] = usePrompt();
  const [formDirty, setFormDirty] = useState(false);
  const [relations, setRelations] = useState<ISelectAddressProps[]>([]);
  const [infoData, setInfoData] = useState<PotentialLeadData>();
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [potentialLeadSub, setPotentialLeadSub] = useState<
    string | null | undefined
  >(undefined);
  const isAnlSubRef = React.useRef<boolean>(false);
  const isAnlSub = potentialLeadSub === 'ANL';

  React.useEffect(() => {
    isAnlSubRef.current = isAnlSub;
  }, [isAnlSub]);

  useEffect(() => {
    const { accessToken } = authenticationService.getUserToken() || {};
    if (!accessToken) {
      pushUrl('/login', search);
    }
  }, [pushUrl, search]);

  const [allowCriminalRecordConsent, setAllowCriminalRecordConsent] = useState(
    true,
  );

  const goToMainPage = () => pushUrl('/', search);
  const goToLoginPage = () => pushUrl('/login', search);

  const buildUpdateInfoPayloadV2 = React.useCallback(
    (values: IPersonalInformationRequest): UpdatePersonalInfo => {
      const t = values.taxAddress || {};
      const ec = values.emergencyContact || {};
      const sa = values.preferredServiceArea || {};

      const addrProvinceId = pickId(provinceOptions, t.province);
      const addrDistrictId = pickId(districtsOptions, t.district);
      const addrSubDistrictId = pickId(subDistrictsOptions, t.subDistrict);
      const postalCodeNum = t.postalCode
        ? Number(String(t.postalCode).replace(/\D/g, ''))
        : undefined;

      const emergencyRelationId =
        Number(pickId(relations, ec.relationship)) || 0;

      const serviceProvince = pickId(provinceOptions, sa.province);
      const serviceDistrict = sa.districtId
        ? Number(sa.districtId)
        : pickId(districtOptionsSA, sa.district);
      const storeSubDistrict = sa.subDistrictId
        ? Number(sa.subDistrictId)
        : pickId(subDistrictOptionsSA, sa.subDistrict);

      const storeId =
        Number(sa.storeCode ?? 0) ||
        Number(storeOptionsSA.find(s => s.value === sa.storeName)?._id ?? 0);

      return {
        tab: 'personal_info',
        useIdCardAddress: t.isAddressAccordingToIDCard ? 1 : 0,
        address: t.isAddressAccordingToIDCard
          ? undefined
          : (t.address || '').trim(),
        province: t.isAddressAccordingToIDCard ? undefined : addrProvinceId,
        district: t.isAddressAccordingToIDCard ? undefined : addrDistrictId,
        subDistrict: t.isAddressAccordingToIDCard
          ? undefined
          : addrSubDistrictId,
        postalCode: t.isAddressAccordingToIDCard ? undefined : postalCodeNum,

        // emergency
        emergencyContactName: (ec.name || '').trim(),
        emergencyContactTel: (ec.phone || '').replace(/\D/g, ''),
        emergencyContactRelation: emergencyRelationId,

        // service area
        serviceProvince,
        serviceDistrict,
        storeSubDistrict,
        storeId,
      };
    },
    [
      provinceOptions,
      districtsOptions,
      subDistrictsOptions,
      districtOptionsSA,
      subDistrictOptionsSA,
      storeOptionsSA,
      relations,
    ],
  );

  const formik = useFormik<IPersonalInformationRequest>({
    initialValues: {
      taxAddress: {
        isAddressAccordingToIDCard: false,
        postalCode: undefined,
        province: undefined,
        district: undefined,
        subDistrict: undefined,
        address: undefined,
      },
      emergencyContact: {
        name: '',
        phone: '',
        relationship: '',
      },
      preferredServiceArea: {
        province: undefined,
        district: undefined,
        districtId: undefined,
        subDistrict: undefined,
        subDistrictId: undefined,
        storeCode: undefined,
        storeName: undefined,
      },
      isAllowEmergencyContact: false,
    },
    enableReinitialize: true,
    validationSchema: validatePersonalInfo,
    validateOnMount: true,
    validate: values =>
      validatePersonalInfo
        .validate(values)
        .then(() => {})
        .catch(err =>
          err.inner.reduce(
            (acc: Record<string, string>, curr: Yup.ValidationError) => {
              acc[curr.path] = curr.message;
              return acc;
            },
            {},
          ),
        ),

    onSubmit: async (formValues, action) => {
      const payload = buildUpdateInfoPayloadV2(formValues);
      if (saveLoading) return;
      setSaveLoading(true);
      try {
        setShowPrompt(false);

        let payloadWithCompany: UpdatePersonalInfo = {
          ...payload,
          companyId: prevCompany ?? 1,
        };

        if (isAnlSubRef.current) {
          const buSiteId = String(
            formValues.preferredServiceArea?.storeCode ?? '',
          );
          if (buSiteId) {
            const checkRes = await addressService.getCheckStore(buSiteId);

            if (checkRes?.success === false) {
              message.warning(checkRes?.message || 'ไม่พบข้อมูลสาขา');
              return;
            }
            const companyCheck = checkRes?.data?.companyId;
            const resolvedCompanyId = companyCheck ?? prevCompany ?? 1;

            const isPrevCompany =
              prevCompany != undefined && prevCompany === resolvedCompanyId;

            const shouldClearStore23 = !isPrevCompany;

            console.log(
              'isPrevCompany=======',
              isPrevCompany,
              prevCompany,
              resolvedCompanyId,
            );

            payloadWithCompany = {
              ...payload,
              companyId: resolvedCompanyId,
              ...(shouldClearStore23 ? { storeId2: null, storeId3: null } : {}),
            };
          } // end if (buSiteId)
        } // end if (isAnlSubRef.current)

        const res = await addressService.updateInfo(payloadWithCompany);
        const out = (res as any)?.data ?? res;

        if (out?.success === false) {
          const msg =
            out?.errorMessageList?.[0] || out?.message || 'บันทึกไม่สำเร็จ';
          message.error(msg);
          return;
        }

        message.success(out?.message || 'บันทึกข้อมูลเรียบร้อยแล้ว');
        goToMainPage();
      } catch (err) {
        const status = err?.response?.status ?? err?.status;
        if (status === STATUS_RESPONSE.UNAUTHORIZED) {
          await authenticationService.logout();
          goToLoginPage();
          return;
        }
        const apiMsg =
          err?.response?.data?.message ||
          err?.message ||
          'เกิดข้อผิดพลาด ไม่สามารถบันทึกข้อมูลได้';
        message.error(apiMsg);
        console.error('[update-info] error:', err);
      } finally {
        setSaveLoading(false);
      }
    },
  });

  const getEmergencyRelations = async () => {
    try {
      return await addressService.EmergencyRelations();
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    const { dirty, isSubmitting } = formik;
    const isDirty = !isSubmitting && dirty;
    setFormDirty(isDirty);
    setIsShowModal(isDirty);
    setShowPrompt(isDirty);
  }, [formik]);

  const getAddressDistricts = async (provinceId: string) => {
    try {
      return await addressService.districts({
        provinceId,
      });
    } catch (err) {
      return null;
    }
  };

  const getDistrict = async (provinceId: string) => {
    const response = await getAddressDistricts(provinceId);
    const districts = response;
    if (!districts) return;
    const _districts = districts.dataList.map(district => ({
      _id: district.id.toString(),
      title: district.district_name_th,
      value: district.district_name_th,
    }));
    setDistrictsOptions(_districts);
    return _districts;
  };

  const getAddressSubDistricts = async (districtId: string) => {
    try {
      return await addressService.subDistricts({
        districtId,
      });
    } catch (err) {
      return null;
    }
  };

  const getSubDistrict = async (districtId: string) => {
    const response = await getAddressSubDistricts(districtId);
    const subDistricts = response;
    if (!subDistricts) return;
    const _subDistricts = subDistricts.dataList.map(subDistrict => ({
      _id: subDistrict.id.toString(),
      title: subDistrict.sub_district_name_th,
      value: subDistrict.sub_district_name_th,
      postalcode: subDistrict.tms_sub_district_id?.toString() ?? '',
    }));
    setSubDistrictsOptions(_subDistricts);
    return _subDistricts;
  };

  const getPostalCodes = async (subDistrictId: string) => {
    try {
      const response = await addressService.postalCodes({ subDistrictId });
      if (!response || response.dataList.length === 0) return [];
      const _postalCodes = response.dataList.map(item => ({
        _id: item.id.toString(),
        title: item.zipcode ?? '',
        value: item.zipcode ?? '',
      }));
      setPostalCodeOptions(_postalCodes);
      return _postalCodes;
    } catch (error) {
      console.error('Error fetching postal codes:', error);
      return [];
    }
  };

  const getServiceAreaDistricts = async (provinceId: string) => {
    const response = await getAddressDistricts(provinceId);
    if (!response) {
      return [] as ISelectAddressProps[];
    }
    const filtered = (response.dataList || []).filter(
      (d: any) => String(d.province_id) === String(provinceId),
    );

    const districtOptions = filtered.map((d: any) => ({
      _id: d.id.toString(),
      title: d.district_name_th,
      value: d.district_name_th,
    }));
    setDistrictOptionsSA(districtOptions);
    return districtOptions;
  };

  const getServiceAreaSubDistricts = async (districtId: string) => {
    const response = await getAddressSubDistricts(districtId);
    if (!response) {
      setSubDistrictOptionsSA([]);
      return [] as ISelectAddressProps[];
    }
    const raw = response.dataList || [];

    const filtered = raw.filter(
      (s: any) => String(s.district_id) === String(districtId),
    );

    const subOptions = filtered.map((s: any) => ({
      _id: s.id.toString(),
      title: s.sub_district_name_th,
      value: s.sub_district_name_th,
      tms_id: s.tms_sub_district_id,
    }));
    setSubDistrictOptionsSA(subOptions);
    return subOptions as ISelectAddressProps[];
  };

  const getStoresBySub = async (
    leadInfo?: PotentialLeadData['data'],
  ): Promise<StoreOption[]> => {
    try {
      const subcontractorPrefix = String(
        leadInfo?.potentialLead?.subcontractor?.prefix || '',
      ).trim();
      const potentialLeadSub = String(
        leadInfo?.potentialLead?.sub || '',
      ).trim();
      const prefix = [subcontractorPrefix, potentialLeadSub].find(
        value => value && !/^\d+$/.test(value),
      );

      // storeId is a selected branch identifier, not a subcontractor prefix.
      // Some lead-info responses return a subdistrict id in potentialLead.sub
      // after saving. Never send that numeric id as the store prefix.
      if (!prefix) {
        setStoreOptionsSA([]);
        return [];
      }

      setLoadingStores(true);

      const isAnl = prefix.toUpperCase() === 'ANL';
      const res = await addressService.getStoreNew({
        ...(prefix ? { prefix } : {}),
        buCustomerId: isAnl ? '3,2' : '3',
      });

      const list = (Array.isArray(res)
        ? res
        : (res as any)?.dataList || []) as any[];

      const options: StoreOption[] = list.map((s: any) => {
        const code = String(s.storeCode ?? s.id ?? s.site_code ?? '');
        const name = String(s.storeName ?? s.site_name ?? '').trim();
        const siteCode = String(s.site_code ?? '').trim();
        const addr = [
          s.province_name_th ? `จังหวัด ${s.province_name_th}` : '',
          s.district_name_th ? ` อำเภอ/เขต ${s.district_name_th}` : '',
          s.sub_district_name_th ? ` ตำบล/แขวง ${s.sub_district_name_th}` : '',
        ]
          .filter(Boolean)
          .join(' ');
        const searchBag = [
          code,
          name,
          s.province_name_th,
          s.district_name_th,
          s.sub_district_name_th,
          s.site_code,
          s.site_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        const display = `${siteCode} – ${name}`;

        return {
          _id: code,
          value: code,
          title: display,
          label: (
            <div className="line-1">
              <strong
                style={{ display: 'block', fontSize: 16, lineHeight: 1.2 }}
              >
                {siteCode} – {name}
              </strong>
              <span className="line-2" style={{ fontSize: 12, color: '#999' }}>
                {addr}
              </span>
            </div>
          ),
          data: { code, name, addr, _search: searchBag, raw: s },
        };
      });

      setStoreOptionsSA(options);
      return options;
    } catch (e) {
      setStoreOptionsSA([]);
      return [];
    } finally {
      setLoadingStores(false);
    }
  };

  const getInfo = async (provinceList?: any[], relations?: any[]) => {
    try {
      const response = await mainService.getInfo();
      const currentSub = response?.data?.potentialLead?.sub ?? null;
      setPotentialLeadSub(currentSub);
      if (!response?.data?.potentialLeadInfo) return;

      const infoData = response.data.potentialLeadInfo;

      const relationFind = relations?.find(
        f => f._id == infoData.emergencyRelationId,
      );

      formik.setFieldValue('emergencyContact', {
        name: infoData?.emergencyContactName ?? '',
        phone: infoData?.emergencyContactTel ?? '',
        relationship: relationFind?.title ?? '',
      });

      setIsApproved(infoData.statusCode === 'approved');
      setPrevCompany(infoData.companyId ?? undefined);

      const provinceId = infoData?.province?.toString();
      const districtId = infoData?.district?.toString();
      const subDistrictId = infoData?.subDistrict?.toString();

      const provinceFind = provinceList?.find(p => p._id == provinceId);

      let districtFind = null;
      let subDistrictFind = null;

      if (provinceFind?._id) {
        const resDistrict = await getDistrict(provinceFind._id);

        if (resDistrict?.length) {
          districtFind = resDistrict.find(d => d._id == districtId);

          if (districtFind?._id) {
            const resSubDistrict = await getSubDistrict(districtFind._id);

            if (resSubDistrict?.length) {
              subDistrictFind = resSubDistrict.find(
                s => s._id == subDistrictId,
              );
            }
          }
        }
      }

      const isUseIdCardAddress = Number(infoData?.useIdCardAddress) === 1;

      formik.setFieldValue('taxAddress', {
        isAddressAccordingToIDCard: isUseIdCardAddress,
        address: infoData?.address ?? '',
        province: provinceFind?.title ?? undefined,
        district: districtFind?.title ?? undefined,
        subDistrict: subDistrictFind?.title ?? undefined,
        postalCode: infoData?.postalCode ?? undefined,
      });

      let storeFind = null;

      const storeList = await getStoresBySub(response.data);

      const districtNames = parseStringList(
        response?.data?.potentialLeadInfo?.areaDistrict ??
          response?.data?.lead_detail_area?.districts ??
          response?.data?.leadDetailArea?.districts ??
          response?.data?.potentialLeadInfo?.districts,
      );
      let serviceSubDistrictList: ISelectAddressProps[] = [];
      if (districtNames.length > 0) {
        const subDistrictResponse = await addressService.subDistricts({
          districtNames,
        });
        const unique = new Map<string, ISelectAddressProps>();
        (subDistrictResponse?.dataList || []).forEach(item => {
          const title = String(item.sub_district_name_th || '').trim();
          if (!title) return;
          const districtName = String(
            item.district_name_th ??
              item.district?.name ??
              item.district?.district_name_th ??
              'อำเภอ/เขตอื่นๆ',
          ).trim();
          const key = `${districtName}|${title}`;
          if (unique.has(key)) return;
          unique.set(key, {
            _id: String(item.id),
            title,
            value: title,
            districtName,
          });
        });
        serviceSubDistrictList = [...unique.values()];
      }
      if (storeList?.length) {
        storeFind = storeList.find(f => f._id == infoData?.storeId?.toString());
      }

      let savedSubDistrict = serviceSubDistrictList.find(
        item =>
          String(item._id) ===
          String(
            infoData?.storeSubDistrict ?? infoData?.serviceSubDistrict ?? '',
          ),
      );
      const storeSubDistrictName = String(
        storeFind?.data?.raw?.sub_district_name_th || '',
      ).trim();
      const storeSubDistrict = serviceSubDistrictList.find(
        item => item.title === storeSubDistrictName,
      );

      // On the first navigation areaDistrict can arrive without enough data to
      // build the selected subdistrict option. Keep the saved value visible by
      // deriving its option from the selected store instead of requiring reload.
      if (!savedSubDistrict && !storeSubDistrict && storeSubDistrictName) {
        const rawStore = storeFind?.data?.raw;
        savedSubDistrict = {
          _id: String(
            infoData?.storeSubDistrict ??
              infoData?.serviceSubDistrict ??
              rawStore?.sub_district_id ??
              rawStore?.tms_sub_district_id ??
              storeSubDistrictName,
          ),
          title: storeSubDistrictName,
          value: storeSubDistrictName,
          districtName: String(
            rawStore?.district_name_th || 'อำเภอ/เขตอื่นๆ',
          ).trim(),
        };
        serviceSubDistrictList = [...serviceSubDistrictList, savedSubDistrict];
      }

      setSubDistrictOptionsSA(serviceSubDistrictList);

      formik.setFieldValue('preferredServiceArea', {
        storeCode: storeFind?.value ?? undefined,
        storeName: storeFind?.value ?? undefined,
        subDistrict: savedSubDistrict?.title ?? storeSubDistrict?.title,
        subDistrictId:
          savedSubDistrict?._id ?? storeSubDistrict?._id ?? undefined,
      });

      await formik.validateForm();
    } catch (e) {
      console.log('===== Debug getInfo error: ', e);
    } finally {
      setLoadingPage(false);
    }
  };

  const hasPersonalInfoErrors = React.useMemo(
    () => !validatePersonalInfo.isValidSync(formik.values),
    [formik.values],
  );

  const getProvinces = async () => {
    // provinces
    const response = await getAddressProvinces();
    const provinces = response;
    if (provinces) {
      const _provinces = provinces.dataList.map((p: any) => ({
        _id: p.id.toString(),
        title: p.province_name_th,
        value: p.province_name_th,
      }));
      setProvinceOptions(_provinces);
    }
  };

  useEffect(() => {
    const getEmergencyRelation = async () => {
      const relations = await getEmergencyRelations();
      if (!relations) return;
      const _relations = relations.dataList.map(relation => ({
        _id: relation.id.toString(),
        value: relation.id.toString(),
        title: relation.name,
      }));
      setRelations(_relations);
    };

    getProvinces();
    getEmergencyRelation();
  }, []);

  useEffect(() => {
    if (relations.length > 0 && provinceOptions.length > 0) {
      getInfo(provinceOptions, relations);
    }
  }, [relations, provinceOptions]);

  const errors = formik.errors || ({} as any);

  const handleUnauthorized = useCallback(async status => {
    if (status === STATUS_RESPONSE.UNAUTHORIZED) {
      await authenticationService.logout();
      goToMainPage();
    }
  }, []);

  // ---------- Tax address handlers ----------
  const taxAddress = { ...formik.values.taxAddress };

  const handleSelectProvince = (provinceSelected: string, option: any) => {
    getDistrict(option._id);
    taxAddress.province = provinceSelected;
    taxAddress.district = undefined;
    taxAddress.subDistrict = undefined;
    taxAddress.postalCode = undefined;
    formik.setFieldTouched('taxAddress.district', true);
    formik.setFieldValue('taxAddress', taxAddress);
  };

  const handleSelectDistrict = (districtSelected: string, option: any) => {
    getSubDistrict(option._id);
    taxAddress.district = districtSelected;
    taxAddress.subDistrict = undefined;
    taxAddress.postalCode = undefined;
    formik.setFieldTouched('taxAddress.subDistrict', true);
    formik.setFieldValue('taxAddress', taxAddress);
  };

  const handleSelectSubDistrict = async (selectedTitle: string) => {
    const subDistrictId = findSubDistrictIdByTitle(selectedTitle);
    if (!subDistrictId) return;
    await getPostalCodes(subDistrictId);
    formik.setFieldValue('taxAddress.subDistrict', selectedTitle);
    formik.setFieldValue('taxAddress.postalCode', undefined);
  };

  const handleSelectPostalCode = (postCodeSelected: string) => {
    taxAddress.postalCode = postCodeSelected;
    formik.setFieldTouched('taxAddress.postalCode', true);
    formik.setFieldValue('taxAddress', taxAddress);
  };

  const handleCheckAddressAccordingToIDCard = (
    arg: CheckboxChangeEvent | boolean,
  ) => {
    const checked = typeof arg === 'boolean' ? arg : arg.target.checked;

    const prev = formik.values.taxAddress || {};
    const next = {
      ...prev,
      isAddressAccordingToIDCard: checked,
      address: checked ? undefined : prev.address,
      province: checked ? undefined : prev.province,
      district: checked ? undefined : prev.district,
      subDistrict: checked ? undefined : prev.subDistrict,
      postalCode: checked ? undefined : prev.postalCode,
    };

    formik.setFieldValue('taxAddress', next);
  };

  const shouldDisplayErrorMessage = (key: keyof IPersonalInformationRequest) =>
    getIn(formik.touched, key) && getIn(formik.errors, key);

  const handleSelectedProvinceSA = async (value: string, option: any) => {
    setDistrictOptionsSA([]);
    setSubDistrictOptionsSA([]);

    const resolvedProvinceId =
      option?._id ??
      provinceOptions.find(p => p.value === value || p.title === value)?._id;

    formik.setFieldValue('preferredServiceArea', {
      province: value,
      district: undefined,
      districtId: undefined,
      subDistrict: undefined,
      subDistrictId: undefined,
      storeCode: undefined,
      storeName: undefined,
    });

    if (!resolvedProvinceId) {
      console.warn('[SA] Province id not found for value:', value);
      return;
    }

    await getServiceAreaDistricts(resolvedProvinceId);
  };

  const handleSelectedDistrictSA = async (value: string, option: any) => {
    const resolvedDistrictId =
      option?._id ??
      districtOptionsSA.find(o => o.value === value || o.title === value)?._id;

    formik.setFieldValue('preferredServiceArea', {
      ...formik.values.preferredServiceArea,
      district: value,
      districtId: resolvedDistrictId,
      subDistrict: undefined,
      subDistrictId: undefined,
      storeCode: undefined,
      storeName: undefined,
    });

    // clear dep options
    setSubDistrictOptionsSA([]);

    if (!resolvedDistrictId) {
      console.warn('[SA] District id not found for value:', value);
      return;
    }

    await getServiceAreaSubDistricts(resolvedDistrictId as string);
  };

  const isTaxAddressComplete = () => {
    const t = formik.values.taxAddress;
    if (t?.isAddressAccordingToIDCard) return true;
    return Boolean(
      t?.address &&
        t?.province &&
        t?.district &&
        t?.subDistrict &&
        t?.postalCode,
    );
  };

  const isEmergencyContactComplete = () => {
    const ec = formik.values.emergencyContact;
    return Boolean(ec?.name && ec?.phone && ec?.relationship);
  };

  const isServiceAreaComplete = () => {
    return true;
  };

  const shouldDisabledSubmit = () =>
    disableEdit ||
    formik.isSubmitting ||
    !allowCriminalRecordConsent ||
    formik.isValidating ||
    hasPersonalInfoErrors ||
    !isTaxAddressComplete() ||
    !isServiceAreaComplete() ||
    !isEmergencyContactComplete();

  const groupedSubDistrictOptionsSA = React.useMemo(() => {
    const groups = new Map<string, ISelectAddressProps[]>();
    subDistrictOptionsSA.forEach(option => {
      const group = option.districtName || 'อำเภอ/เขตอื่นๆ';
      groups.set(group, [...(groups.get(group) || []), option]);
    });
    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b, 'th'))
      .map(([label, options]) => ({
        label,
        options: options.sort((a, b) => a.title.localeCompare(b.title, 'th')),
      }));
  }, [subDistrictOptionsSA]);
  const selectedStore = (loadingStores ? [] : storeOptionsSA).find(
    (o: any) => o.value === formik.values.preferredServiceArea?.storeName,
  );

  const lat = selectedStore?.data?.raw?.latitude ?? null;

  const lng = selectedStore?.data?.raw?.longitude ?? null;

  const gmapsUrl =
    lat && lng
      ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
      : undefined;

  return (
    <>
      {loadingPage && <Loading />}

      {/*<HeaderPortal*/}
      {/*  documentsReviewStatusName={*/}
      {/*    personalInformation?.data?.documentsReviewStatus?.status*/}
      {/*  }*/}
      {/*  formik={formik as FormikProps<FormikValues>}*/}
      {/*/>*/}

      <DriverPortalLayout>
        <ReturnButton />

        {/*{isDocumentRejected(personalInformation) && (*/}
        {/*  <Alert*/}
        {/*    type="error"*/}
        {/*    title="กรุณาแก้ไขข้อมูล"*/}
        {/*    bulletLists={[`${getDocumentRejectMessage(personalInformation)}`]}*/}
        {/*    style={{ marginBottom: theme.spacing.xl }}*/}
        {/*  />*/}
        {/*)}*/}

        <Title>ข้อมูลส่วนตัว</Title>
        <TextRegular>
          กรุณากรอกข้อมูล และอัพโหลดเอกสารที่มีเครื่องหมาย * ให้ครบถ้วน แล้วกด
          &quot;บันทึก&quot;
        </TextRegular>
        <Divider />

        <TextBold
          color={theme.palettes.black}
          display={'block'}
          margin={`0px 0px ${theme.spacing.md}`}
        >
          ที่อยู่ในการจัดส่งใบกำกับภาษี
        </TextBold>

        <form onSubmit={formik.handleSubmit}>
          <Checkbox
            disabled={disableEdit || isApproved}
            style={{ marginBottom: theme.spacing.xl }}
            onChange={handleCheckAddressAccordingToIDCard}
            checked={formik?.values?.taxAddress?.isAddressAccordingToIDCard}
          >
            <TextRegular
              fontSize={theme.fontSize.xs}
              color={theme.palettes.black85}
            >
              ใช้ที่อยู่ตามบัตรประชาชน
            </TextRegular>
          </Checkbox>

          {!formik?.values?.taxAddress?.isAddressAccordingToIDCard && (
            <>
              <FormItem
                label="ที่อยู่"
                required
                labelCol={{ span: 24 }}
                colon={false}
                validateStatus={
                  shouldDisplayErrorMessage('taxAddress.address' as any)
                    ? 'error'
                    : ''
                }
                help={
                  shouldDisplayErrorMessage('taxAddress.address' as any) &&
                  (errors as any)?.taxAddress?.address
                }
              >
                <Input
                  disabled={disableEdit || isApproved}
                  size="large"
                  name="taxAddress.address"
                  placeholder="ตัวอย่าง 111/11 ซอยวัดทุ่ง ถนนสุขุมวิท"
                  value={formik?.values?.taxAddress?.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </FormItem>

              <FormItem
                label="จังหวัด"
                required
                labelCol={{ span: 24 }}
                colon={false}
                validateStatus={
                  shouldDisplayErrorMessage('taxAddress.province' as any)
                    ? 'error'
                    : ''
                }
                help={
                  shouldDisplayErrorMessage('taxAddress.province' as any) &&
                  (errors as any)?.taxAddress?.province
                }
              >
                <Select
                  disabled={disableEdit || isApproved}
                  size="large"
                  showSearch
                  placeholder="เลือกจังหวัด"
                  optionFilterProp="children"
                  options={provinceOptions}
                  value={formik?.values?.taxAddress?.province}
                  onChange={handleSelectProvince}
                  onBlur={formik.handleBlur}
                  onDropdownVisibleChange={open => {
                    if (!open) {
                      formik.setFieldTouched(
                        'taxAddress.province',
                        true,
                        false,
                      );
                      const val = getIn(formik.values, 'taxAddress.province');
                      formik.setFieldError(
                        'taxAddress.province',
                        val ? undefined : 'กรุณาเลือกจังหวัด',
                      );
                    }
                  }}
                />
              </FormItem>

              <FormItem
                label="เขต/อำเภอ"
                required
                labelCol={{ span: 24 }}
                colon={false}
                validateStatus={
                  shouldDisplayErrorMessage('taxAddress.district' as any)
                    ? 'error'
                    : ''
                }
                help={
                  shouldDisplayErrorMessage('taxAddress.district' as any) &&
                  (errors as any)?.taxAddress?.district
                }
              >
                <Select
                  size="large"
                  showSearch
                  placeholder="เลือกเขต/อำเภอ"
                  optionFilterProp="children"
                  options={districtsOptions}
                  value={formik?.values?.taxAddress?.district}
                  onChange={handleSelectDistrict}
                  disabled={
                    disableEdit ||
                    isApproved ||
                    Boolean(!formik?.values?.taxAddress?.province)
                  }
                  onBlur={formik.handleBlur}
                />
              </FormItem>

              <FormItem
                label="แขวง/ตำบล"
                required
                labelCol={{ span: 24 }}
                colon={false}
                validateStatus={
                  shouldDisplayErrorMessage('taxAddress.subDistrict' as any)
                    ? 'error'
                    : ''
                }
                help={
                  shouldDisplayErrorMessage('taxAddress.subDistrict' as any) &&
                  (errors as any)?.taxAddress?.subDistrict
                }
              >
                <Select
                  size="large"
                  showSearch
                  placeholder="เลือกแขวง/ตำบล"
                  optionFilterProp="children"
                  options={subDistrictsOptions}
                  value={formik?.values?.taxAddress?.subDistrict}
                  onChange={handleSelectSubDistrict}
                  disabled={
                    disableEdit ||
                    isApproved ||
                    Boolean(!formik?.values?.taxAddress?.district)
                  }
                  onBlur={formik.handleBlur}
                />
              </FormItem>

              <FormItem
                label="รหัสไปรษณีย์"
                required
                labelCol={{ span: 24 }}
                colon={false}
                validateStatus={
                  shouldDisplayErrorMessage('taxAddress.postalCode' as any)
                    ? 'error'
                    : ''
                }
                help={
                  shouldDisplayErrorMessage('taxAddress.postalCode' as any) &&
                  (errors as any)?.taxAddress?.postalCode
                }
              >
                <Col span={11}>
                  <Select
                    fontFamily={theme.fonts.NotoSands}
                    size="large"
                    showSearch
                    placeholder="เลือกรหัสไปรษณีย์"
                    optionFilterProp="children"
                    options={postalCodeOptions}
                    value={formik?.values?.taxAddress?.postalCode}
                    onChange={handleSelectPostalCode}
                    disabled={
                      disableEdit ||
                      isApproved ||
                      Boolean(!formik?.values?.taxAddress?.subDistrict)
                    }
                    onBlur={formik.handleBlur}
                  />
                </Col>
              </FormItem>
            </>
          )}

          <Divider />

          <TextBold
            color={theme.palettes.black}
            display={'block'}
            margin={`0px 0px ${theme.spacing.xl}`}
            fontSize={theme.fontSize.md}
          >
            บุคคลที่ติดต่อได้ในกรณีฉุกเฉิน
          </TextBold>

          <FormItem
            label="ชื่อ - นามสกุล บุคคลที่ติดต่อได้ในกรณีฉุกเฉิน (ภาษาไทย)"
            required
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={
              shouldDisplayErrorMessage('emergencyContact.name' as any)
                ? 'error'
                : ''
            }
            help={
              shouldDisplayErrorMessage('emergencyContact.name' as any) &&
              (errors as any)?.emergencyContact?.name
            }
          >
            <Input
              disabled={disableEdit || isApproved}
              size="large"
              name="emergencyContact.name"
              placeholder="กรอกชื่อ - นามสกุล บุคคลที่ติดต่อได้ในกรณีฉุกเฉิน"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik?.values?.emergencyContact?.name}
            />
          </FormItem>

          <FormItem
            label="ความสัมพันธ์"
            required
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={
              formik.touched?.emergencyContact?.relationship &&
              (errors as any)?.emergencyContact?.relationship
                ? 'error'
                : ''
            }
            help={
              formik.touched?.emergencyContact?.relationship &&
              (errors as any)?.emergencyContact?.relationship
            }
          >
            <Select
              size="large"
              placeholder="เลือกความสัมพันธ์"
              optionFilterProp="label"
              options={relations}
              onChange={val =>
                formik.setFieldValue('emergencyContact.relationship', val)
              }
              onBlur={() =>
                formik.setFieldTouched('emergencyContact.relationship', true)
              }
              value={formik.values?.emergencyContact?.relationship || undefined}
              allowClear
              disabled={disableEdit || isApproved}
            />
          </FormItem>

          <FormItem
            label="เบอร์โทรศัพท์ของบุคคลที่ติดต่อได้ในกรณีฉุกเฉิน"
            required
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={
              shouldDisplayErrorMessage('emergencyContact.phone' as any)
                ? 'error'
                : ''
            }
            help={
              shouldDisplayErrorMessage('emergencyContact.phone' as any) &&
              (errors as any)?.emergencyContact?.phone
            }
          >
            <Input
              disabled={disableEdit || isApproved}
              size="large"
              name="emergencyContact.phone"
              placeholder="ตัวอย่าง 0800000000"
              onBlur={formik.handleBlur}
              maxLength={10}
              onChange={formik.handleChange}
              value={formik?.values?.emergencyContact?.phone}
            />
          </FormItem>

          <FormItem required labelCol={{ span: 24 }} colon={false}>
            <InputCheckBox
              name="isAllowEmergencyContact"
              text=" ข้าพเจ้าขอรับรองว่าบุคคลที่ข้าพเจ้าระบุข้อมูลเป็น “บุคคลที่ติดต่อในกรณีฉุกเฉิน”อ่านประกาศเกี่ยวกับการคุ้มครองข้อมูลส่วนบุคคล (Privacy Notice) ของผู้ประกอบการขนส่งที่ข้าพเจ้าสังกัดแล้ว "
              onChecked={() =>
                setAllowCriminalRecordConsent(!allowCriminalRecordConsent)
              }
              disabled={disableEdit || isApproved}
              checked={allowCriminalRecordConsent}
            />
          </FormItem>

          <Divider />

          <div>
            <TextBold
              color={theme.palettes.black}
              fontSize={theme.fontSize.md}
              margin={`0px 0px ${theme.spacing.xl} `}
              display={'block'}
            >
              พื้นที่ที่ต้องการให้บริการ
            </TextBold>
            <TextBold
              color={theme.palettes.black45}
              margin={`0px 0px ${theme.spacing.sm}`}
              display={'block'}
            >
              ลำดับที่ 1
            </TextBold>
            <Row gutter={24}>
              <Col span={24}>
                <FormItem
                  label="แขวง/ตำบล"
                  required={false}
                  labelCol={{ span: 24 }}
                  colon={false}
                >
                  <AntdSelect
                    size="large"
                    allowClear
                    showSearch
                    value={formik.values.preferredServiceArea?.subDistrict}
                    options={groupedSubDistrictOptionsSA}
                    optionLabelProp="title"
                    onChange={(value, option) => {
                      formik.setFieldValue('preferredServiceArea', {
                        ...formik.values.preferredServiceArea,
                        subDistrict: value || undefined,
                        subDistrictId: value ? (option as any)?._id : undefined,
                        storeCode: undefined,
                        storeName: undefined,
                      });
                    }}
                    placeholder="เลือกแขวง/ตำบล"
                    disabled={disableEdit || isApproved}
                  />
                </FormItem>
                <FormItem
                  label="ร้าน/สาขา"
                  required={false}
                  labelCol={{ span: 24 }}
                  colon={false}
                >
                  <AntdSelect
                    className="store-select"
                    size="large"
                    showSearch
                    value={formik.values.preferredServiceArea?.storeName}
                    allowClear
                    options={loadingStores ? [] : storeOptionsSA}
                    optionLabelProp="title"
                    onBlur={() =>
                      formik.setFieldTouched(
                        'preferredServiceArea.storeName',
                        true,
                      )
                    }
                    filterOption={(input, option) => {
                      const bag =
                        (option as any)?.data?._search ??
                        String(option?.value ?? '').toLowerCase();
                      return bag.includes(input.trim().toLowerCase());
                    }}
                    onChange={(_, option) => {
                      const data = (option as any)?.data;
                      formik.setFieldValue('preferredServiceArea', {
                        ...formik.values.preferredServiceArea,
                        storeCode: data?.code,
                        storeName: (option as any)?.value, // "CODE – NAME"
                      });
                      formik.setFieldTouched(
                        'preferredServiceArea.storeName',
                        false,
                      );
                    }}
                    placeholder="เลือกสาขา (ค้นได้จากรหัส, ชื่อ, จังหวัด, อำเภอ/เขต, ตำบล/แขวง)"
                    disabled={disableEdit || isApproved}
                  />
                </FormItem>
              </Col>
              <Col span={24}>
                {gmapsUrl ? (
                  <a
                    href={gmapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1677ff', textDecoration: 'underline' }}
                  >
                    ดูตำแหน่งสาขา
                  </a>
                ) : (
                  <TextRegular style={{ color: '#999' }}>
                    ดูตำแหน่งสาขา
                  </TextRegular>
                )}
              </Col>
            </Row>
          </div>

          <Divider />

          <Button
            block
            htmlType="submit"
            disabled={shouldDisabledSubmit() || isApproved}
            loading={saveLoading}
            size="large"
            type={shouldDisabledSubmit() || isApproved ? 'default' : 'primary'}
          >
            บันทึก
          </Button>
        </form>
      </DriverPortalLayout>
      {formDirty && <ConfirmReturnModal isShow={isShowModal} />}
    </>
  );
};

export default PersonalInformationPage;
